import { useEffect, useRef, useState } from "react";
import type { RecoveryOffer, RecoveryState } from "@cuekards/core/recovery";
import {
  EMPTY_RECOVERY_STATE,
  describeOffer,
  formatBytes,
} from "@cuekards/core/recovery";

/**
 * The interrupted-session recovery offer (ticket 13, FR-47).
 *
 * It sits at the top of the control window because on the launch where it
 * appears it is the most important thing there: a take that was already shot and
 * cannot be shot again is sitting on disk waiting to be turned into a file, and
 * a user who came here to load their next script would otherwise scroll straight
 * past it.
 *
 * Nothing here decides anything. The host scans at launch, owns the offers, and
 * runs the remuxes; this shows them and sends back which one the user picked.
 */
export function RecoveryPanel({
  disabled = false,
}: {
  disabled?: boolean;
}): JSX.Element | null {
  const [state, setState] = useState<RecoveryState>(EMPTY_RECOVERY_STATE);

  useEffect(() => {
    let cancelled = false;
    // The scan runs at launch, before this window exists, so the state has to be
    // pulled as well as followed.
    void window.cuekards.getRecoveryState().then((s) => {
      if (!cancelled) setState(s);
    });
    const off = window.cuekards.onRecoveryStateChanged(setState);
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  // Nothing was interrupted, which is the ordinary case — so the section is
  // absent rather than an empty panel saying so on every launch.
  if (state.offers.length === 0) return null;

  return (
    <section className="recovery" aria-label="Interrupted recordings">
      <h2>Unfinished recordings</h2>
      <p className="recovery-lede">
        {state.offers.length === 1 ? "A take was" : "Some takes were"} left
        unfinished — cuekards was closed or interrupted before the video file was
        written. The footage is still here.
      </p>
      <ul className="recovery-list">
        {state.offers.map((offer) => (
          <Offer key={offer.id} offer={offer} disabled={disabled} />
        ))}
      </ul>
    </section>
  );
}

function Offer({
  offer,
  disabled,
}: {
  offer: RecoveryOffer;
  disabled: boolean;
}): JSX.Element {
  const working = offer.status === "recovering";
  return (
    <li className={`recovery-offer status-${offer.status}`}>
      <div className="recovery-what">
        <span className="recovery-when">{describeOffer(offer)}</span>
        <span className="recovery-size">{formatBytes(offer.bytes)}</span>
      </div>

      {offer.status === "recovered" ? (
        <>
          <p className="recovery-saved" role="status">
            Recovered to {offer.output}
          </p>
          <div className="recovery-actions">
            <button
              type="button"
              onClick={() => window.cuekards.dismissSession(offer.id)}
            >
              Dismiss
            </button>
          </div>
        </>
      ) : (
        <>
          {offer.status === "failed" && (
            <p className="recovery-error" role="alert">
              {offer.message}
            </p>
          )}
          <div className="recovery-actions">
            <button
              type="button"
              className="recover"
              onClick={() => window.cuekards.recoverSession(offer.id)}
              disabled={working || disabled}
              title={
                disabled
                  ? "Recovery is unavailable while recording"
                  : "Turn this take's footage into a playable MP4"
              }
            >
              {working
                ? "Recovering…"
                : offer.status === "failed"
                  ? "Try again"
                  : "Recover"}
            </button>
            {/* Declining deletes nothing — see the note below, which says so
              * rather than leaving the user to hope. */}
            <button
              type="button"
              onClick={() => window.cuekards.dismissSession(offer.id)}
              disabled={working}
            >
              Not now
            </button>
            {/* Greyed with Recover rather than with "Not now": the host refuses
              * both mid-take, and a delete that stayed live under a greyed
              * Recover would be the one button still working being the one that
              * cannot be undone. */}
            <DeleteButton id={offer.id} disabled={working || disabled} />
          </div>
          <p className="recovery-note">
            “Not now” keeps the footage at {offer.chunkPath} and offers it again
            next time cuekards starts. Nothing is deleted.
          </p>
        </>
      )}
    </li>
  );
}

/**
 * The one control in the offer that throws footage away (web ticket 13).
 *
 * Two presses rather than one, and no `confirm()` dialog. The take on the other
 * side of this button is a performance that cannot be repeated and the panel is
 * a list of similar-looking rows, so a single misplaced click is exactly the
 * failure to design against — but a modal is worse than it looks here: it is the
 * kind of prompt people dismiss without reading, and on the desktop it would
 * steal focus from a window that might be counting into a take.
 *
 * So the button asks by *becoming* the question, in the place the pointer
 * already is. It goes back to asking after a few seconds, because a control left
 * armed is a trap for whoever comes back to the keyboard.
 */
function DeleteButton({
  id,
  disabled,
}: {
  id: string;
  disabled: boolean;
}): JSX.Element {
  const [armed, setArmed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <button
      type="button"
      className={armed ? "delete armed" : "delete"}
      disabled={disabled}
      onClick={() => {
        clearTimeout(timer.current);
        if (armed) {
          setArmed(false);
          window.cuekards.discardSession(id);
          return;
        }
        setArmed(true);
        timer.current = setTimeout(() => setArmed(false), 5_000);
      }}
      title={
        armed
          ? "This cannot be undone"
          : "Delete this take's footage — it cannot be undone"
      }
    >
      {armed ? "Delete for good?" : "Delete"}
    </button>
  );
}
